/**
 * Turns what the KPI engine returns into the shapes the report widgets take.
 *
 * Everything here is a pure selector over already-fetched data; fetching lives
 * in `useProjectIntelligence`. Two rules carried over from the backend:
 *
 * - A KPI that could not be computed has no row at all (spec §3.3). Missing is
 *   therefore "unknown", never 0, and must render as a dash rather than a zero.
 * - Labels and categories come with the values, so nothing here hardcodes what
 *   a KPI is called - only which keys this page reads.
 */
import {
  KpiHistoryDto,
  KpiScope,
  KpiValueDto,
  MilestoneDto,
} from '../../api/model';
import { ComparisonDatum, ProjectHealthKey } from './reportData';

/** The keys this page reads. Mirrors com.zequent.qualiq.intelligence.kpis.KpiKeys. */
export const KPI = {
  projectProgress: 'op.project_progress',
  projectProgressWeighted: 'op.project_progress_weighted',
  phaseProgress: 'op.phase_progress',
  loProgress: 'op.lo_progress',
  releaseProgress: 'op.release_progress',
  sprintProgress: 'op.sprint_progress',
  projectHealthScore: 'ph.project_health_score',
  projectHealthStatus: 'ph.project_health_status',
  executionScore: 'ex.execution_performance_score',
  qualityScore: 'qa.quality_assurance_score',
  readinessScore: 'pr.project_readiness_score',
  dependencyScore: 'dh.dependency_health_score',
  projectQaCompletion: 'qa.project_qa_completion',
  scheduleReliability: 'ex.schedule_reliability',
  projectHealthTrend: 'tr.project_health_trend',
  // Score Confidence (§14 step 17): how much of each score's weight was computable
  healthConfidence: 'ph.score_confidence',
  readinessConfidence: 'pr.score_confidence',
  executionConfidence: 'ex.score_confidence',
  qualityConfidence: 'qa.score_confidence',
  dependencyConfidence: 'dh.score_confidence',
  // Execution
  expectedPhaseProgress: 'ex.expected_phase_progress',
  phasePerformanceScore: 'ex.phase_performance_score',
  projectProgressPerformance: 'ex.project_progress_performance',
  overduePhaseRate: 'ex.overdue_phase_rate',
  overdueDeliverableRate: 'ex.overdue_deliverable_rate',
  overduePhaseCount: 'ex.overdue_phase_count',
  overdueDeliverableCount: 'ex.overdue_deliverable_count',
  criticalDeliveryPerformance: 'ex.critical_delivery_performance',
  criticalAttentionRate: 'ex.critical_attention_rate',
  deliverableCount: 'op.deliverable_count',
  // Quality assurance
  phaseQaCompletion: 'qa.phase_qa_completion',
  qualityAssuredPhaseProgress: 'qa.quality_assured_phase_progress',
  qaCoverage: 'qa.coverage',
  qaTimeliness: 'qa.timeliness',
  delayedCheckpointRate: 'qa.delayed_checkpoint_rate',
  // Dependency health
  dependencyIntegrity: 'dh.dependency_integrity',
  hardDependencyViolationRate: 'dh.hard_dependency_violation_rate',
  parallelDependencyRiskRate: 'dh.parallel_dependency_risk_rate',
  overduePredecessorRate: 'dh.overdue_predecessor_rate',
  // Milestones
  milestoneProgress: 'op.milestone_progress',
  milestoneStatus: 'ex.milestone_status',
  daysToMilestone: 'ex.days_to_milestone',
  overdueMilestoneRate: 'ex.overdue_milestone_rate',
  // Detailmasken (2026-09-22)
  expectedProjectProgress: 'ex.expected_project_progress',
  phaseProgressWeighted: 'op.phase_progress_weighted',
  phaseProgressVariance: 'ex.phase_progress_variance',
  phaseExecutionStatus: 'ex.phase_execution_status',
  loExecutionStatus: 'ex.lo_execution_status',
  projectExecutionStatus: 'ex.project_execution_status',
  phaseStatus: 'op.phase_status',
  loProgressVariance: 'ex.lo_progress_variance',
  expectedLoProgress: 'ex.expected_lo_progress',
  qualityAssuredProjectProgress: 'qa.quality_assured_project_progress',
  phaseQaTimeliness: 'qa.phase_timeliness',
  phaseDelayedCheckpointRate: 'qa.phase_delayed_checkpoint_rate',
  planningReadinessLo: 'pr.planning_readiness_lo',
  scheduleConsistency: 'pr.schedule_consistency',
  ownershipReadiness: 'pr.ownership_readiness',
  structureReadiness: 'pr.structure_readiness',
  qualityReadiness: 'pr.quality_readiness',
  projectPhaseDataReadiness: 'pr.project_phase_data_readiness',
  projectLoDataReadiness: 'pr.project_lo_data_readiness',
  phaseDataReadiness: 'pr.phase_data_readiness',
  loDataReadiness: 'pr.lo_data_readiness',
  directDownstreamImpact: 'dh.direct_downstream_impact',
} as const;

/**
 * The four dimension scores the spec's "Health by dimension" widget is made of
 * (§12), each with the Score Confidence that belongs to it.
 *
 * Confidence is per score, not one number for the project: a Project Health of
 * 100 % confidence can still rest on an Execution score that only had half its
 * components, and saying so is the whole point of the KPI.
 */
export const HEALTH_DIMENSIONS = [
  { key: KPI.executionScore, confidenceKey: KPI.executionConfidence, labelKey: 'execution' },
  { key: KPI.qualityScore, confidenceKey: KPI.qualityConfidence, labelKey: 'quality' },
  { key: KPI.readinessScore, confidenceKey: KPI.readinessConfidence, labelKey: 'readiness' },
  { key: KPI.dependencyScore, confidenceKey: KPI.dependencyConfidence, labelKey: 'dependency' },
] as const;

export type KpiIndex = Map<string, KpiValueDto>;

/**
 * Indexes project-scoped values by key. Scope instances (per phase, per
 * deliverable) are read with `valuesForScope` instead, since there is one row
 * per instance.
 */
export function indexProjectKpis(values: KpiValueDto[] | undefined): KpiIndex {
  const index: KpiIndex = new Map();
  for (const value of values ?? []) {
    if (value.scopeType === KpiScope.PROJECT && value.key) {
      index.set(value.key, value);
    }
  }
  return index;
}

/**
 * When the engine last wrote a value for this project - the newest
 * {@code computedAt} across everything it returned.
 *
 * Since the recompute moved off the write path onto a queue, a page can be
 * showing numbers from just before the edit that is still being processed.
 * This is what lets it say so rather than quietly implying the values are
 * current. Undefined means nothing has ever been computed.
 */
export function lastComputedAt(values: KpiValueDto[] | undefined): string | undefined {
  let newest: string | undefined;
  for (const value of values ?? []) {
    if (value.computedAt && (newest === undefined || value.computedAt > newest)) {
      newest = value.computedAt;
    }
  }
  return newest;
}

export function valuesForScope(
  values: KpiValueDto[] | undefined,
  key: string,
  scope: KpiScope,
): KpiValueDto[] {
  return (values ?? []).filter((v) => v.key === key && v.scopeType === scope);
}

/** The numeric value of a project-scoped KPI, or undefined when it was not computable. */
export function numeric(index: KpiIndex, key: string): number | undefined {
  const value = index.get(key)?.numericValue;
  return typeof value === 'number' ? value : undefined;
}

export function categorical(index: KpiIndex, key: string): string | undefined {
  return index.get(key)?.categoricalValue ?? undefined;
}

/** The engine's own label for a KPI, so the UI never re-names one it displays. */
export function labelOf(index: KpiIndex, key: string, fallback: string): string {
  return index.get(key)?.label || fallback;
}

const HEALTH_KEY_BY_STATUS: Record<string, ProjectHealthKey> = {
  ON_TRACK: 'onTrack',
  WATCH: 'watch',
  AT_RISK: 'atRisk',
  CRITICAL: 'critical',
};

/** Maps ph.project_health_status onto the i18n key the health pill uses. */
export function healthKeyFromStatus(value: string | undefined): ProjectHealthKey | undefined {
  return value ? HEALTH_KEY_BY_STATUS[value] : undefined;
}

/** A percentage for display, or an em dash when the KPI was not computable. */
export function formatPercent(value: number | undefined): string {
  return typeof value === 'number' ? `${Math.round(value)}%` : '—';
}

/**
 * Formats a tr.* KPI as a signed delta. Those KPIs are the change against the
 * 7-day lookback, so an absent one means "no comparable snapshot yet" - shown
 * as a flat dash rather than an implied zero change.
 */
export function formatDelta(value: number | undefined): {
  delta: string;
  direction: 'up' | 'down' | 'flat';
} {
  if (typeof value !== 'number') return { delta: '—', direction: 'flat' };
  const rounded = Math.round(value * 10) / 10;
  if (rounded === 0) return { delta: '±0', direction: 'flat' };
  return {
    delta: `${rounded > 0 ? '+' : ''}${rounded}`,
    direction: rounded > 0 ? 'up' : 'down',
  };
}

export interface HistoryPoint {
  date: string;
  value: number;
}

/**
 * One history series as chartable points. Days where the KPI was not computable
 * carry no point at all, so the line breaks rather than dipping through a zero.
 */
export function historyPoints(series: KpiHistoryDto | undefined): HistoryPoint[] {
  return (series?.points ?? [])
    .filter((p) => typeof p.numericValue === 'number' && p.date)
    .map((p) => ({ date: p.date as string, value: p.numericValue as number }));
}

/** Any scope's numeric value by scope id, for widgets that join several KPIs per row. */
export function numericByScopeId(
  values: KpiValueDto[] | undefined,
  key: string,
  scope: KpiScope,
): Map<number, number> {
  const byId = new Map<number, number>();
  for (const value of valuesForScope(values, key, scope)) {
    if (value.scopeId != null && typeof value.numericValue === 'number') {
      byId.set(value.scopeId, value.numericValue);
    }
  }
  return byId;
}

export function categoricalByScopeId(
  values: KpiValueDto[] | undefined,
  key: string,
  scope: KpiScope,
): Map<number, string> {
  const byId = new Map<number, string>();
  for (const value of valuesForScope(values, key, scope)) {
    if (value.scopeId != null && value.categoricalValue) {
      byId.set(value.scopeId, value.categoricalValue);
    }
  }
  return byId;
}

export type MilestoneStatus = 'ACHIEVED' | 'READY' | 'ON_TRACK' | 'AT_RISK' | 'OVERDUE';

export interface MilestoneRow {
  id: number;
  name: string;
  deadline?: string;
  progress?: number;
  daysToDeadline?: number;
  status?: MilestoneStatus;
}

/**
 * One row per milestone, joining the milestone list with its KPI values.
 * Sorted by deadline, undated last - the order a milestone plan is read in.
 */
export function milestoneRows(
  values: KpiValueDto[] | undefined,
  milestones: MilestoneDto[] | undefined,
): MilestoneRow[] {
  const progress = numericByScopeId(values, KPI.milestoneProgress, KpiScope.MILESTONE);
  const days = numericByScopeId(values, KPI.daysToMilestone, KpiScope.MILESTONE);
  const status = categoricalByScopeId(values, KPI.milestoneStatus, KpiScope.MILESTONE);

  return [...(milestones ?? [])]
    .filter((m): m is MilestoneDto & { id: number } => m.id != null)
    .map((m) => ({
      id: m.id,
      name: m.name || `#${m.id}`,
      deadline: m.deadline as string | undefined,
      progress: progress.get(m.id),
      daysToDeadline: days.get(m.id),
      status: status.get(m.id) as MilestoneStatus | undefined,
    }))
    .sort((a, b) => {
      if (!a.deadline) return 1;
      if (!b.deadline) return -1;
      return a.deadline.localeCompare(b.deadline);
    });
}

/**
 * Two bars per phase, joining any two phase-scoped KPIs - e.g. progress
 * against quality-assured progress. Phases are listed when the first KPI is
 * computable; a missing second value leaves that bar out rather than at zero.
 */
export function phaseComparisonBars(
  values: KpiValueDto[] | undefined,
  firstKey: string,
  secondKey: string,
  phaseNames: Map<number, string>,
): ComparisonDatum[] {
  const second = numericByScopeId(values, secondKey, KpiScope.PHASE);
  return valuesForScope(values, firstKey, KpiScope.PHASE)
    .filter((v) => v.scopeId != null && typeof v.numericValue === 'number')
    .map((v) => {
      const secondValue = second.get(v.scopeId as number);
      return {
        label: phaseNames.get(v.scopeId as number) ?? `#${v.scopeId}`,
        planned: Math.round(v.numericValue as number),
        actual: secondValue != null ? Math.round(secondValue) : undefined,
      };
    });
}

